import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule }  from '@angular/common';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { DataService } from '../../../services/data.service';

interface PaymentReceipt {
  receiptNumber:   string;
  merchantOrderId: string;
  phonePeOrderId:  string;
  state:           string;
  paymentStatus:   string;
  studentName:     string;
  studentEmail:    string;
  studentPhone:    string;
  programName:     string;
  academicYear:    string;
  feeComponents:   { label: string; amount: any }[];
  subtotal:        number;
  taxAmount:       number;
  processingFee:   number;
  totalAmount:     number;
  paymentMode:     string | null;
  paidAt:          string;
  institutionName: string;
}

@Component({
  selector: 'app-payment-receipt',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './payment-receipt.html',
  styleUrl: './payment-receipt.scss',
})
export class PaymentReceiptComponent implements OnInit {
  private readonly route       = inject(ActivatedRoute);
  // private readonly paymentSvc  = inject(PaymentService);
  public dataService = inject(DataService);

  isLoading    = signal(true);
  receipt      = signal<PaymentReceipt | null>(null);
  state        = signal<any>('PENDING');
  errorMsg     = signal('');
  erp_mgr: any = this.dataService.erp_mgr;

  ngOnInit(): void {
    const qp        = this.route.snapshot.queryParams;
    const orderId   = qp['orderId']   ?? '';
    const ppOrderId = qp['ppOrderId'] ?? '';
    const urlState  = (qp['state']   ?? qp['status'] ?? 'COMPLETED');
    const amount    = +(qp['amount']  ?? 0);
    const mode      = (qp['mode'] ?? qp['method'] ?? 'UPI_INTENT');

    this.state.set(urlState);

    if (orderId) {
      const mockReceipt = this.getMockReceipt(orderId);
      const receipt: PaymentReceipt = {...mockReceipt,
        merchantOrderId: orderId,
        phonePeOrderId:  ppOrderId || mockReceipt.phonePeOrderId,
        state:           urlState,
        paymentStatus:   urlState === 'COMPLETED' ? 'SUCCESS' : urlState === 'FAILED' ? 'FAILED' : 'PENDING',
        // totalAmount:     amount || mockReceipt.totalAmount,
        paymentMode:     mode,
        studentName:     qp['name']    || mockReceipt.studentName,
        studentEmail:    qp['email']   || mockReceipt.studentEmail,
        studentPhone:    qp['phone']   || mockReceipt.studentPhone,
        programName:     qp['program'] || mockReceipt.programName,
        receiptNumber:   'RCPT-' + orderId.slice(-8).toUpperCase(),
        paidAt:          new Date().toISOString(),
      };
      this.receipt.set(receipt);
      this.isLoading.set(false);
    } else {
      this.errorMsg.set('Invalid payment session. Please contact support.');
      this.isLoading.set(false);
    }
  }

  get isSuccess(): boolean { return this.state() === 'COMPLETED'; }
  get isFailed():  boolean { return this.state() === 'FAILED' || this.state() === 'EXPIRED'; }

  formatDate(iso: string): string {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  }

  fmt(amount: number): string { return this.formatCurrency(amount); }

  getMethodLabel(m: any | null): string {
    return this.getPaymentModeLabel(m);
  }

  getMethodIcon(m: any | null): string {
    return this.getPaymentModeIcon(m);
  }

  print(): void { window.print(); }

  downloadPdf(): void {
    // In production, call GET /api/v1/payments/:orderId/receipt.pdf
    // For demo, just print
    window.print();
  }
//--------------------------------------------------------------------------------









  getMockReceipt(orderId: string): PaymentReceipt {
    const phonePeOrderId = 'OMO' + Math.floor(Math.random() * 1e16);
    return {
      receiptNumber:   'RCPT-' + orderId.slice(-8).toUpperCase(),
      merchantOrderId: orderId,
      phonePeOrderId,
      state:           'COMPLETED',
      paymentStatus:   'SUCCESS',
      studentName:     'Rajesh Kumar',
      studentEmail:    'dev.macwill@email.com',
      studentPhone:    '9876543210',
      programName:     'Software Engineer',
      academicYear:    '2025-26',
      feeComponents: [
        { label: 'Plan / Package', amount: 500 },
        { label: 'Subscription',   amount: '12 Months' },
        { label: 'Students',       amount: 1400 },
        { label: 'Users',           amount: 4 },
        { label: 'Base Amount',     amount: 7000 },
        { label: 'Discount',        amount: 0 },
        { label: 'Other Charges',   amount: 0   },
      ],
      subtotal:        7000,
      taxAmount:       (7000 * 18) / 100,
      processingFee:   18,
      totalAmount:     7000 + (7000 * 18) / 100,
      paymentMode:     'UPI_INTENT',
      paidAt:          new Date().toISOString(),
      institutionName: this.erp_mgr.account_name
    };
  }
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(amount);
  }
  getPaymentModeLabel(mode: any | null | undefined): string {
    const labels: Partial<Record<any, string>> = {
      UPI_INTENT:   'UPI (App)',
      UPI_COLLECT:  'UPI Collect',
      UPI_QR:       'UPI QR Code',
      CARD:         'Credit / Debit Card',
      TOKEN:        'Saved Card (Token)',
      NET_BANKING:  'Net Banking',
      CREDIT_LINE:  'Credit Line / EMI',
    };
    return mode ? (labels[mode] ?? mode) : '—';
  }
  getPaymentModeIcon(mode: any | null | undefined): string {
    const icons: Partial<Record<any, string>> = {
      UPI_INTENT:   'bi-phone-fill',
      UPI_COLLECT:  'bi-qr-code-scan',
      UPI_QR:       'bi-qr-code',
      CARD:         'bi-credit-card-2-front-fill',
      TOKEN:        'bi-shield-lock-fill',
      NET_BANKING:  'bi-bank2',
      CREDIT_LINE:  'bi-cash-coin',
    };
    return mode ? (icons[mode] ?? 'bi-cash') : 'bi-cash';
  }
}
